import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Users, Plus, ArrowLeft, LogIn } from "lucide-react";
import { useLang } from "@/lib/lang";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import NavBar from "@/components/NavBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GamesTeams() {
  const { lang } = useLang();
  const { user, login } = useAuth();
  const [teams, setTeams] = useState([]);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const T = lang === "fr" ? FR : EN;

  const load = () => axios.get(`${API}/games/teams/list`).then(r => setTeams(r.data.teams));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!user) { toast.error(T.loginFirst); return; }
    if (name.trim().length < 3) { toast.error(T.nameShort); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/games/teams/create`, { name: name.trim() }, { withCredentials: true });
      toast.success(T.created);
      setName(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || T.error); }
    setCreating(false);
  };

  const join = async (id) => {
    if (!user) { toast.error(T.loginFirst); return; }
    try {
      await axios.post(`${API}/games/teams/${id}/join`, {}, { withCredentials: true });
      toast.success(T.joined);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || T.error); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="games-teams-page">
      <NavBar />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <Link to="/games" className="text-sm text-zinc-500 hover:text-[#8b6f3d] flex items-center gap-1"><ArrowLeft size={14} /> {T.back}</Link>
        <h1 className="font-serif-mystery text-4xl mt-3 mb-6">🏆 {T.title}</h1>

        <div className="border border-zinc-200 rounded-2xl p-5 mb-8 bg-amber-50/40">
          <h2 className="font-serif-mystery text-xl mb-3">{T.createTeam}</h2>
          {user ? (
            <div className="flex gap-2">
              <input data-testid="team-name-input" value={name} onChange={e => setName(e.target.value)}
                placeholder={T.teamName} className="flex-1 border border-zinc-300 rounded-full h-11 px-4" />
              <Button data-testid="team-create-btn" onClick={create} disabled={creating}
                className="bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-11 px-4">
                <Plus size={16} className="mr-1" /> {T.create}
              </Button>
            </div>
          ) : (
            <Button onClick={login} className="bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-11 px-5">
              <LogIn size={16} className="mr-2" /> {T.loginToCreate}
            </Button>
          )}
          <p className="text-xs text-zinc-500 mt-2">{T.maxMembers}</p>
        </div>

        <h2 className="font-serif-mystery text-2xl mb-4">{T.allTeams}</h2>
        <div className="space-y-2" data-testid="teams-list">
          {teams.length === 0 && <p className="text-center text-zinc-500 py-6">{T.noTeams}</p>}
          {teams.map(t => (
            <div key={t.id} className="flex items-center gap-3 p-4 rounded-xl border border-zinc-200 hover:border-[#8b6f3d] transition-colors" data-testid={`team-row-${t.id}`}>
              <Users size={18} className="text-[#8b6f3d]" />
              <div className="flex-1 min-w-0">
                <div className="font-medium">{t.name}</div>
                <div className="text-xs text-zinc-500">{t.members_count}/10 {T.members} · {(t.total_score || 0).toLocaleString()} pts</div>
              </div>
              {user && t.members_count < 10 && !user.team_id && (
                <Button data-testid={`join-team-${t.id}`} onClick={() => join(t.id)}
                  variant="outline" className="rounded-full h-9 border-[#8b6f3d] text-[#8b6f3d]">{T.join}</Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
const FR = { back: "Retour aux jeux", title: "Équipes", createTeam: "Créer une équipe", teamName: "Nom de l'équipe (3-32 caractères)", create: "Créer", loginToCreate: "Connecte-toi pour créer une équipe", maxMembers: "Une équipe peut contenir jusqu'à 10 joueurs.", allTeams: "Toutes les équipes", noTeams: "Aucune équipe pour le moment. Crée la première !", members: "membres", join: "Rejoindre", loginFirst: "Connecte-toi avec Google d'abord.", nameShort: "Nom trop court (min 3 caractères).", created: "🎉 Équipe créée !", joined: "✅ Équipe rejointe !", error: "Erreur." };
const EN = { back: "Back to games", title: "Teams", createTeam: "Create a team", teamName: "Team name (3-32 chars)", create: "Create", loginToCreate: "Sign in to create a team", maxMembers: "A team can hold up to 10 players.", allTeams: "All teams", noTeams: "No teams yet. Create the first one!", members: "members", join: "Join", loginFirst: "Sign in with Google first.", nameShort: "Name too short (min 3 chars).", created: "🎉 Team created!", joined: "✅ Team joined!", error: "Error." };
